import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus } from "lucide-react";
import { api, formatError } from "../../lib/api";

const StatusBadge = ({ rsvp }) => {
  if (!rsvp) {
    return <span className="text-[10px] uppercase tracking-[0.2em] text-stone border border-stone/30 rounded-full px-3 py-1">Pendiente</span>;
  }
  return rsvp.attending ? (
    <span className="text-[10px] uppercase tracking-[0.2em] text-sage border border-sage/40 rounded-full px-3 py-1">Confirmado</span>
  ) : (
    <span className="text-[10px] uppercase tracking-[0.2em] text-clay-deep border border-clay-deep/40 rounded-full px-3 py-1">No asiste</span>
  );
};

export const GuestsPanel = () => {
  const [guests, setGuests] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/admin/guests").then((r) => setGuests(r.data.guests)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await api.post("/admin/guests", { name: name.trim() });
      setName("");
      toast.success("Invitado agregado con su código");
      load();
    } catch (e2) {
      toast.error(formatError(e2));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`¿Eliminar a ${g.name}?`)) return;
    await api.delete(`/admin/guests/${g.id}`).catch(() => {});
    toast.success("Invitado eliminado");
    load();
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    toast.success(`Código ${code} copiado`);
  };

  const confirmed = guests.filter((g) => g.rsvp?.attending).length;
  const declined = guests.filter((g) => g.rsvp && !g.rsvp.attending).length;
  const pending = guests.length - confirmed - declined;

  const stats = [
    { label: "Invitados", value: guests.length, testid: "stat-total" },
    { label: "Confirmados", value: confirmed, testid: "stat-confirmed" },
    { label: "No asisten", value: declined, testid: "stat-declined" },
    { label: "Pendientes", value: pending, testid: "stat-pending" },
  ];

  return (
    <div data-testid="guests-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-cream border border-clay/15 rounded-3xl p-6 text-center">
            <p data-testid={s.testid} className="font-serif text-4xl text-clay">{s.value}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-stone">{s.label}</p>
          </div>
        ))}
      </div>

      <form onSubmit={add} className="mt-8 flex flex-col sm:flex-row gap-3">
        <input
          data-testid="add-guest-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre del invitado (ej. Familia Pérez)"
          className="flex-1 bg-cream border border-clay/20 rounded-full px-6 py-4 text-sm text-ink placeholder:text-stone/50 focus:outline-none focus:border-clay transition-colors"
        />
        <button
          data-testid="add-guest-submit"
          type="submit"
          disabled={loading}
          className="rounded-full bg-clay text-cream px-8 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-clay-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <UserPlus size={16} /> {loading ? "Agregando…" : "Agregar invitado"}
        </button>
      </form>

      <div className="mt-8 bg-cream border border-clay/15 rounded-3xl overflow-hidden">
        <table data-testid="guests-table" className="w-full text-sm">
          <thead>
            <tr className="border-b border-clay/10 text-left">
              <th className="px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-stone font-medium">Nombre</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-stone font-medium">Código</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-stone font-medium">Estado</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-[0.25em] text-stone font-medium hidden md:table-cell">Mensaje</th>
              <th className="px-6 py-4" />
            </tr>
          </thead>
          <tbody>
            {guests.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-stone font-serif italic">
                  Aún no hay invitados. Agrega el primero arriba.
                </td>
              </tr>
            )}
            {guests.map((g) => (
              <tr key={g.id} data-testid={`guest-row-${g.code}`} className="border-b border-clay/5 last:border-0">
                <td className="px-6 py-4 font-medium text-ink">{g.name}</td>
                <td className="px-6 py-4">
                  <button
                    data-testid={`copy-code-${g.code}`}
                    onClick={() => copy(g.code)}
                    className="font-mono tracking-[0.2em] text-clay hover:text-clay-deep transition-colors inline-flex items-center gap-2"
                  >
                    {g.code} <Copy size={13} />
                  </button>
                </td>
                <td className="px-6 py-4">
                  <StatusBadge rsvp={g.rsvp} />
                </td>
                <td className="px-6 py-4 text-stone max-w-[220px] truncate hidden md:table-cell">
                  {g.rsvp?.message || "—"}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    data-testid={`delete-guest-${g.code}`}
                    onClick={() => remove(g)}
                    className="text-stone hover:text-clay-deep transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
